import { BriefcaseBusiness, EyeOff, RefreshCw, Search, Users } from "lucide-react";
import type { ApiItem } from "../utils/applicationCards";
import {
  DreamBoard,
  DreamCompany,
  openRoles,
  plural,
  shortTimeAgo,
  soonestDeadline,
  stagesReached,
  stepsOf,
  type Step,
  toMillis,
  trackingState,
  unseenNewRoles,
} from "../utils/dreamCompanies";
import { TAG } from "./applicationShared";
import { CardSteps, CompanyLogo, DeadlineChip, StageBar } from "./dreamShared";
import { cn } from "../lib/utils";

type Props = {
  company: DreamCompany;
  /** Tracker cards for this company */
  applications: ApiItem[];
  /** boards/{boardKey}, for "Checked 2h ago" */
  board?: DreamBoard;
  onOpen: () => void;
  onStepsChange: (steps: Step[]) => void;
};

// One company on the Dream Companies grid. Open roles come from its job board
// (company.tracking, written by the backend); the footer says how fresh they are.
export default function DreamCompanyCard({
  company,
  applications,
  board,
  onOpen,
  onStepsChange,
}: Props) {
  const state = trackingState(company);
  const newRoles = unseenNewRoles(company);
  const deadline = soonestDeadline(company);
  const people = company.people?.length || 0;
  const checkedAt = toMillis(board?.lastCheckedAt);
  const checked = checkedAt ? shortTimeAgo(new Date(checkedAt).toISOString()) : "";

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${company.name}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        "group flex cursor-pointer flex-col rounded-[14px] border bg-melt-paper p-4 font-grotesk text-melt-text1 shadow-[0_1px_0_rgba(92,52,23,.04)] outline-none transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-[0_12px_24px_-18px_rgba(92,52,23,.45)] focus-visible:ring-2 focus-visible:ring-melt-caramel/30",
        newRoles.length ? "border-melt-caramel/40" : "border-melt-text1/[.12] hover:border-melt-text1/20"
      )}
    >
      <div className="flex items-start gap-3">
        <CompanyLogo name={company.name} domain={company.domain} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-medium leading-tight">{company.name}</h3>
          {company.domain && <p className="mt-0.5 truncate text-[12.5px] text-melt-text3">{company.domain}</p>}
        </div>
        <div className="flex flex-none flex-col items-end gap-1">
          {newRoles.length > 0 && (
            <span
              className={cn(TAG, "inline-flex items-center gap-1.5 bg-melt-caramel/[.14] text-[#8A5210]")}
              title={newRoles.map((r) => r.title).join("\n")}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-melt-caramel" />
              {newRoles.length === 1 ? "New role" : `${newRoles.length} new roles`}
            </span>
          )}
          {deadline !== null && <DeadlineChip days={deadline} />}
        </div>
      </div>

      <div className="mt-3.5">
        <CardSteps steps={stepsOf(company)} onChange={onStepsChange} />
      </div>

      <div className="mt-4">
        <StageBar reached={stagesReached(company, applications)} />
      </div>

      <div className="mt-3.5 flex items-center gap-4 text-[12.5px] text-melt-text2">
        <span className="inline-flex items-center gap-1.5">
          <BriefcaseBusiness size={14} strokeWidth={1.75} className="text-melt-text3" />
          {state === "tracked"
            ? plural(openRoles(company).length, "open role")
            : plural(company.roles?.length || 0, "saved role")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Users size={14} strokeWidth={1.75} className="text-melt-text3" />
          {plural(people, "person", "people")}
        </span>
      </div>
{/* 
      <div className="mt-auto pt-3.5">
        <p className="flex items-center gap-1.5 border-t border-melt-line pt-3 text-[12px] text-melt-text3">
          {state === "untracked" ? (
            <>
              <EyeOff size={13} strokeWidth={1.75} className="flex-none" />
              Can’t track automatically
            </>
          ) : state === "pending" ? (
            <>
              <Search size={13} strokeWidth={1.75} className="flex-none" />
              Looking for their job board…
            </>
          ) : (
            <>
              <RefreshCw size={13} strokeWidth={1.75} className="flex-none" />
              {checked ? `Checked ${checked}` : "First check soon"}
            </>
          )}
        </p>
      </div> */}
    </div>
  );
}
